import React, { createContext, useContext, useState, useCallback } from 'react';
import { ConsoleSection, LogEntry, ConsoleState } from '@/types';

interface ConsoleContextValue extends ConsoleState {
  setSections: (sections: ConsoleSection[]) => void;
  appendSections: (sections: ConsoleSection[]) => void;
  clearSections: () => void;
  addLog: (log: Omit<LogEntry, 'id'>) => void;
  clearLogs: () => void;
  clearAll: () => void;
}

const ConsoleContext = createContext<ConsoleContextValue | undefined>(undefined);

export function ConsoleProvider({ children }: { children: React.ReactNode }) {
  const [sections, setSectionsState] = useState<ConsoleSection[]>([]);
  const [logs, setLogs] = useState<LogEntry[]>([]);

  // Set visualization sections (replaces all)
  const setSections = useCallback((newSections: ConsoleSection[]) => {
    setSectionsState(newSections);
  }, []);

  // Append visualization sections
  const appendSections = useCallback((newSections: ConsoleSection[]) => {
    setSectionsState((prev) => [...prev, ...newSections]);
  }, []);

  // Clear visualization sections
  const clearSections = useCallback(() => {
    setSectionsState([]);
  }, []);

  // Add log entry
  const addLog = useCallback((log: Omit<LogEntry, 'id'>) => {
    const newLog: LogEntry = {
      ...log,
      id: crypto.randomUUID(),
    };

    setLogs((prev) => [...prev, newLog]);
  }, []);

  // Clear logs
  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  // Clear everything
  const clearAll = useCallback(() => {
    setSectionsState([]);
    setLogs([]);
  }, []);

  const value: ConsoleContextValue = {
    sections,
    logs,
    agentStatus: 'offline', // This will be updated by WebSocketContext
    isConnected: false, // This will be updated by WebSocketContext
    setSections,
    appendSections,
    clearSections,
    addLog,
    clearLogs,
    clearAll,
  };

  return (
    <ConsoleContext.Provider value={value}>
      {children}
    </ConsoleContext.Provider>
  );
}

export function useConsole() {
  const context = useContext(ConsoleContext);
  if (context === undefined) {
    throw new Error('useConsole must be used within ConsoleProvider');
  }
  return context;
}
